import { Injectable, inject } from '@angular/core';
import { PermissionService } from 'src/app/services/permission.service';
import { ProcessModuleService } from 'src/app/services/process-module/process-module.service';

/** Inventory pages, in sidebar order. */
export type InventoryPage = 'overview' | 'stock-in-hand' | 'in-out' | 'statement' | 'processing' | 'my-tasks' | 'operators' | 'machines' | 'full-trace';

/**
 * Which right opens which Inventory page (2026-09-16). Mirrors the backend's per-page view rights
 * (PermissionCode.INVENTORY_PAGE_VIEW_RIGHTS) and each page's route guard in inventory-routing.module.ts.
 * Every link or button that leads into an Inventory page from anywhere in the app asks canOpen()
 * first, so a page hidden from a user is never offered to them — and the route guard plus the
 * server still refuse it if someone types the address. Hidden always means no access.
 */
export const INVENTORY_PAGE_RIGHTS: Record<InventoryPage, string> = {
  'overview': 'INVENTORY_OVERVIEW_VIEW',
  // 2026-09-30: Stock in Hand — same right as Overview (reads the same stock numbers).
  'stock-in-hand': 'INVENTORY_OVERVIEW_VIEW',
  'in-out': 'INVENTORY_IN_OUT_VIEW',
  'statement': 'INVENTORY_STATEMENT_VIEW',
  'processing': 'INVENTORY_PROCESSING_VIEW',
  'my-tasks': 'INVENTORY_PROCESSING_VIEW',
  'operators': 'INVENTORY_OPERATORS_VIEW',
  'machines': 'INVENTORY_MACHINES_VIEW',
  'full-trace': 'FULL_TRACE_ACCESS',
};

/** Landing order for a bare /inventory link — the first page the user may open wins. */
const LANDING_ORDER: InventoryPage[] = ['overview', 'in-out', 'statement', 'processing', 'operators', 'machines'];

/**
 * Pages that belong to the Mill / Process module rather than to Inventory generally. Since
 * prompt245 Mill is Forest-only while Inventory starts at Tree, so holding the page right is no
 * longer enough — the tenant's plan has to include the module too.
 */
const PROCESS_PAGES: ReadonlySet<InventoryPage> = new Set<InventoryPage>([
  'processing', 'my-tasks', 'operators', 'machines',
]);

@Injectable({ providedIn: 'root' })
export class InventoryPageAccessService {
  private readonly permissionService = inject(PermissionService);
  private readonly processModuleService = inject(ProcessModuleService);

  /** hasEffective(): RBAC off for the tenant → everything open (same rule as PermissionGuard);
   *  admins already receive every code from /users/me/permissions.
   *
   *  Mill pages additionally require the Process module. Without this, a Tree tenant who holds
   *  INVENTORY_PROCESSING_VIEW would still be offered Processing everywhere — and worse, a bare
   *  /inventory link could LAND them there, only for ProcessModuleGuard to bounce them straight
   *  to Page Not Found. That would break this service's own promise that a page hidden from a
   *  user is never offered to them. */
  canOpen(page: InventoryPage): boolean {
    if (PROCESS_PAGES.has(page) && !this.isProcessAvailable()) return false;
    return this.permissionService.hasEffective(INVENTORY_PAGE_RIGHTS[page]);
  }

  /**
   * Deliberately fails OPEN: only a positively-known "Process is off" hides a mill page.
   *
   * ProcessModuleService caches into a BehaviorSubject that starts false, so reading it before
   * load() completes would tell a Forest user their own Mill pages are closed — a far more
   * damaging error than briefly offering a link a Tree user cannot use. The authoritative check
   * is ProcessModuleGuard, which makes a fresh uncached call on every navigation, plus the
   * backend, which 403s regardless. This only decides what is worth showing.
   */
  private isProcessAvailable(): boolean {
    this.processModuleService.load(); // no-op once already loading or loaded
    if (!this.processModuleService.isResolved) return true; // no answer yet — assume available
    return this.processModuleService.visible;
  }

  /** Any single right, same fail-open rule — for Inventory action buttons on non-Inventory screens. */
  hasRight(code: string): boolean {
    return this.permissionService.hasEffective(code);
  }

  /** First Inventory page this user may open, or null when none. */
  firstAllowedPage(): InventoryPage | null {
    return LANDING_ORDER.find((p) => this.canOpen(p)) ?? null;
  }

  /** For generic navigateTo(url) helpers: '/inventory/<page>…' needs that page's right,
   *  a bare '/inventory' needs any page, everything else is not this service's concern. */
  canOpenUrl(url: string | null | undefined): boolean {
    if (!url) return true;
    const match = /^\/?inventory(?:\/([^/?#]+))?(?:[/?#]|$)/.exec(url);
    if (!match) return true;
    const page = match[1] as InventoryPage | undefined;
    if (!page) return this.firstAllowedPage() !== null;
    return page in INVENTORY_PAGE_RIGHTS ? this.canOpen(page) : true;
  }
}
